<template>
  <div class="c-promotion-detail">
    <div class="buttons flex flex-row justify-around md:justify-between">
      <button type="button" class="twn-button text-xs" @click="goBack">
        Retour
      </button>
      <div>
        <button
          v-if="itemID && !isReadOnly"
          type="button"
          class="twn-button text-xs danger mr-4"
          :disabled="hasPendingStoreRequest"
          @click="showDeleteModal()"
        >
          Supprimer la promotion
        </button>
        <button
          v-if="!isReadOnly || allowedToEditInterns || allowedToEditInternSequence"
          type="button"
          class="twn-button text-xs"
          :disabled="hasPendingStoreRequest"
          @click="save"
        >
          Enregistrer
        </button>
      </div>
    </div>

    <div class="twn-card my-10">
      <div class="flex">
        <div v-if="!itemID" class="uppercase font-principal-bold text-sm">
          Ajouter une promotion
        </div>
        <div v-else-if="isReadOnly" class="uppercase font-principal-bold text-sm">
          Détails de la promotion
        </div>
        <div v-else class="uppercase font-principal-bold text-sm">
          Éditer la promotion
        </div>
      </div>

      <div class="mt-8">
        <div class="flex">
          <!-- Title -->
          <div class="flex-1 mr-8">
            <label>Titre</label>
            <b-form-input
              type="text"
              :plaintext="isReadOnly"
              v-model="promotion.title"
            />
          </div>

          <!-- Course -->
          <div class="flex-1">
            <label>Parcours associé</label>
            <v-select
              v-if="!isReadOnly"
              label="name"
              placeholder="Parcours"
              :options="courseList"
              :filterable="true"
              :reduce="course => course.id"
              v-model="promotion.course"
            />
            <p v-else>
              {{ promotion.course && courseList.filter(c => c.id === promotion.course)[0].name }}
            </p>
          </div>
        </div>

        <div class="flex mt-8">
          <!-- Dates -->
          <div class="flex-1 mr-8">
            <label>Dates de session</label>
            <div class="flex items-center">
              <!-- Start date -->
              <div class="mr-4">Du</div>
              <b-form-datepicker
                v-if="!isReadOnly"
                class="w-64 mr-4"
                :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'long' }"
                v-model="promotion.startDate"
              />
              <p
                v-else-if="promotion.startDate && typeof promotion.startDate === 'string'"
                class="mr-4"
              >
                {{ promotion.startDate.split('-').reverse().join('/') }}
              </p>
              <p
                v-else-if="promotion.startDate && typeof promotion.startDate === 'object'"
                class="mr-4"
              >
                {{ promotion.startDate.toLocaleDateString() }}
              </p>

              <!-- End date -->
              <div class="mr-4">Au</div>
              <b-form-datepicker
                v-if="!isReadOnly"
                class="w-64 mr-4"
                :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'long' }"
                :min="promotion.startDate"
                v-model="promotion.endDate"
              />
              <p v-else-if="promotion.endDate && typeof promotion.endDate === 'string'">
                {{ promotion.endDate.split('-').reverse().join('/') }}
              </p>
              <p v-else-if="promotion.endDate && typeof promotion.endDate === 'object'">
                {{ promotion.endDate.toLocaleDateString() }}
              </p>
            </div>
          </div>

          <div class="flex-1">
            <!-- Type -->
            <label>Type</label>
            <v-select
              v-if="!isReadOnly"
              class="flex-1 mr-auto"
              label="name"
              placeholder="Type de promotion"
              :options="typeList"
              :reduce="type => type.id"
              :clearable="false"
              v-model="promotion.type"
            />
            <p v-else-if="typeList.length > 0 && promotion.type">
              {{ typeList.filter(t => t.id === promotion.type)[0].name }}
            </p>
          </div>
        </div>

        <div class="flex mt-8">
          <div
            v-if="isSuperAdmin"
            class="flex-1 mr-8"
          >
            <!-- Group Managers -->
            <label>Responsables de promo</label>
            <v-select
              id="managers"
              class="twn-select"
              label="name"
              multiple
              :options="managerList"
              :reduce="manager => manager.id"
              v-model="promotion.managers"
            />
          </div>

          <div class="flex-1" />
        </div>

        <div class="mt-8">
          <div class="tabs-triggers flex rounded border border-gray-light">
            <button
              :class="{
                'selected': (currentTab == 'sequences')
              }"
              type="button"
              @click="currentTab = 'sequences'"
            >
              <span v-if="promotionType && promotionType.slug == 'collective_promotion'">Dates de disponibilité des séquences</span>
              <span v-else>Dates limites de planification des séquences</span>
            </button>
            <button
              :class="{
                'selected': (currentTab == 'users')
              }"
              type="button"
              @click="currentTab = 'users'"
            >
              Stagiaires inscrits dans cette promotion
            </button>
            <button
              :class="{
                'selected': (currentTab == 'calendar')
              }"
              type="button"
              @click="currentTab = 'calendar'"
            >
              <span v-if="promotionType && promotionType.slug == 'collective_promotion'">Rattrapages</span>
              <span v-else>Calendrier des stagiaires</span>
            </button>
          </div>

          <PromotionSequenceForm
            v-show="currentTab == 'sequences'"
            :course="course"
            :promotion="promotion"
            :promotion-type="promotionType"
            :is-read-only="isReadOnly"
            @set-sequence-start="onSetSequenceStartDate"
            @set-sequence-end="onSetSequenceEndDate"
          />
          <PromotionInternsForm
            v-show="currentTab == 'users'"
            :promotion="promotion"
            :is-read-only="isReadOnly && !allowedToEditInterns"
            @add-intern="onAddIntern"
            @remove-intern="onRemoveIntern"
          />
          <PromotionInternSequenceForm
            v-show="currentTab == 'calendar'"
            :course="course"
            :promotion="promotion"
            :promotion-type="promotionType"
            :is-read-only="isReadOnly && !allowedToEditInternSequence"
            @add-user-sequence="onAddInternSequence"
            @remove-user-sequence="onRemoveInternSequence"
          />
        </div>
      </div>
    </div>
    
    <div v-if="promotion.id">
      <GroupDashboard
        ref="dashboard"
        :group="promotion"
        :groupType="promotionType"
        :course="course"
      />
    </div>

    <!-- Modals -->
    <b-modal
      ref="delete-modal-detail-promotion"
      class="bootstrap"
      centered
      hide-footer
      id="delete-modal-detail-promotion"
      hide-header
    >
      <div class="d-block text-center my-6 uppercase font-semibold">
        <h3>Confirmer la suppression de "{{promotion.title}}"</h3>
      </div>
      <div class="flex flex-row justify-evenly items-center">
        <button type="button" class="mt-4 twn-button" @click="$bvModal.hide('delete-modal-detail-promotion')" >
          Retour
        </button>
        <button type="button" class="mt-4 twn-button danger" @click="onConfirmDelete">Supprimer</button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import dispatchStoreRequest from "@/mixins/dispatchStoreRequest"
import { GC_GET_SCENARIO_NAME_BY_ID } from '@/graphql/scenario'
import PromotionSequenceForm from "./PromotionSequenceForm"
import PromotionInternsForm from "./PromotionInternsForm"
import PromotionInternSequenceForm from "./PromotionInternSequenceForm"
import GroupDashboard from '@/components/dashboard/Group'
import XLSX from 'xlsx-js-style'

export default {
  props: [ 'itemID' ],
  mixins: [ dispatchStoreRequest ],
  components: {
    PromotionSequenceForm,
    PromotionInternsForm,
    PromotionInternSequenceForm,
    GroupDashboard
  },
  data: () => ({
    // UI State
    currentTab: 'sequences',
    // Local promotion data
    promotion: {
      id: null,
      title: '',
      startDate: null,
      endDate: null,
      course: null,
      type: null,
      creatorId: null,
      sequences: [],
      users: [],
      managers: [],
    },
    course: null,
  }),
  computed: {
    ...mapState('Auth', [ 'userInfo' ]),
    ...mapGetters('Auth', [ 'isSuperAdmin', 'isAdmin' ]),
    ...mapState({
      courseList: state => state.Course.list,
      typeList: state => state.Promotion.typeList,
    }),
    ...mapState('Utils', ['userList']),
    isReadOnly() {
      if (this.isAdmin) {
        // Admin can create group but only edit group that they created
        return !!this.itemID && this.promotion.creatorId !== this.userInfo.id
      }

      return !this.isSuperAdmin
    },
    allowedToEditInterns() {
      // Admin can add/remove interns in other group, but they are limited to themselves or user they created
      return this.isSuperAdmin || this.isAdmin
    },
    allowedToEditInternSequence() {
      // Admin can add/remove intern sequences in other group, but they are limited to themselves or user they created
      return this.isSuperAdmin || this.isAdmin
    },
    promotionType() {
      if (!this.typeList || !this.promotion || !this.promotion.type)
        return null

      return this.typeList.find((type) => (type.id == this.promotion.type))
    },
    managerList() {
      if (!this.userList) {
        return []
      }

      return this.userList.filter((user) => {
        return user.role === 'groupmanager'
      }).map((user) => {
        return {
          id: user.id,
          name: ((user.first_name || '') + ' ' + (user.name || '')),
        }
      })
    },
  },
  watch: {
    itemID: {
      async handler(itemID) {
        if (itemID) {
          // todo: handle invalid UUID response
          await this.dispatchStoreRequest('Promotion/getByID', itemID)

          // Convert remote data to local one
          const remoteData = this.$store.state.Promotion.items[itemID]

          this.promotion.id = itemID

          // todo: handle error
          if (!remoteData)
            return

          this.promotion.title = remoteData.title
          this.promotion.startDate = remoteData.start_date
          this.promotion.endDate = remoteData.end_date
          this.promotion.course = remoteData.course_id
          this.promotion.scenarios = null //Set scenario var for log data
          this.promotion.type = remoteData.promotion_type_id
          this.promotion.creatorId = remoteData.creator_id
          this.promotion.sequences = remoteData.sequences.map((sequence) => {
            return {
              id: sequence.sequence_id,
              startDate: new Date(sequence.start_date),
              endDate: new Date(sequence.end_date),
              userID: sequence.user_id,
            }
          })

          this.promotion.users = remoteData.users.map((user) => {
            return {
              id: user.user_id,
            }
          })

          this.promotion.managers = remoteData.managers.map((manager) => {
            return manager.user_id
          })
        } else {
          const today = new Date()
          const todayNextYear = (new Date())
          todayNextYear.setFullYear(today.getFullYear() + 1)

          // Reset local data
          this.promotion.id = null
          this.promotion.title = ''
          this.promotion.startDate = today
          this.promotion.endDate = todayNextYear
          this.promotion.course = null
          this.promotion.type = null
          this.promotion.creatorId = this.userInfo.id
          this.promotion.sequences = []
          this.promotion.users = []
          this.promotion.managers = []
          this.promotion.scenarios = null
        }
      },
      immediate: true,
    },
    'promotion.startDate': {
      handler(startDate) {
        if (startDate && startDate > this.promotion.endDate) {
          this.promotion.endDate = this.promotion.startDate
        }
      },
      immediate: true,
    },
    'promotion.course': {
      async handler(courseID, oldCourseID) {
        if (courseID) {
          // Set temp data state
          this.course = {}
          
          // Load course data
          this.course = await this.dispatchStoreRequest('Course/GetCourse', courseID)

          // Reset sequences data if needed
          if (oldCourseID && courseID != oldCourseID) {
            this.promotion.sequences = []
          }
        } else {
          this.course = null
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    // Load course and promotion type list
    await this.dispatchStoreRequest('Course/getList')
    await this.dispatchStoreRequest('Promotion/getTypeList')

    // Set default values
    if (!this.itemID) {
      this.promotion.type = (this.typeList.length > 0 ? this.typeList[0].id : null)
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'promotions-list' })
    },
    showDeleteModal() {
      this.$refs['delete-modal-detail-promotion'].show()
    },
    async formatPromotionStatistics(wb, promotionData){
      let sheet_Total = XLSX.utils.aoa_to_sheet([
        [this.promotion.title],
        ["Date de début", "Date de fin"],
        [this.promotion.startDate, this.promotion.endDate],
        [],
        ["Temps moyen des utilisateurs", "Temps cumulé des utilisateurs"],
        [promotionData.formatedAverageCoTime, promotionData.formatedAdditionalCoTime],
        [],
        ["Temps moyen par scénario"]
      ])

      let response = await this.$apollo.query({
        query: GC_GET_SCENARIO_NAME_BY_ID,
        variables: {
            ids: Object.keys(promotionData.averageScenarioTime)
        }
      })

      this.promotion.scenarios = response.data.scenario.reduce((dict, scenario) => {
        dict[scenario.id] = scenario.name
        return dict
      }, {})

      let index = 7;
      //Scenario name => temps
      for (const scenarioID in promotionData.averageScenarioTime) {
        if (Object.hasOwnProperty.call(promotionData.averageScenarioTime, scenarioID)) {
          const scenarioTime = promotionData.averageScenarioTime[scenarioID];
          
          XLSX.utils.sheet_add_aoa(sheet_Total, [
            [this.promotion.scenarios[scenarioID], scenarioTime]
          ], {origin:{r:index, c:0}})
          index++;
        }
      }

      // Get sheet name with excel limitation check
      let sheetName = this.promotion.title

      if (sheetName.length > 26) {
        sheetName = sheetName.substring(0, 26) + '...'
      }

      XLSX.utils.book_append_sheet(wb, sheet_Total, sheetName)
    },
    formatUserStatistics(wb, promotionData){
      for (const userName in promotionData.users) {
        if (Object.hasOwnProperty.call(promotionData.users, userName)) {
          const userData = promotionData.users[userName]

          let sheet_user = XLSX.utils.aoa_to_sheet([
            [(userData.firstName + " " + userName)],
            ["Temps de connexion moyen par jour"],
          ])
          let index = 1;

          for (const dayID in userData.connexionTime) {
            if (Object.hasOwnProperty.call(userData.connexionTime, dayID)) {
              const timePerDay = userData.connexionTime[dayID];
              XLSX.utils.sheet_add_aoa(sheet_user, [
                [dayID, timePerDay]
              ], {origin:{r:index, c:0}})
              index++;
            }
          }

          XLSX.utils.sheet_add_aoa(sheet_user, [
            [],
            ["Temps moyen par scénario"],
          ], {origin:{r:index, c:0}})
          index += 2

          for (const scenarioID in userData.formatedScenarioTimes) {
            if (Object.hasOwnProperty.call(userData.formatedScenarioTimes, scenarioID)) {
              const scenarioTime = userData.formatedScenarioTimes[scenarioID];
              XLSX.utils.sheet_add_aoa(sheet_user, [
                [this.promotion.scenarios[scenarioID], scenarioTime]
              ], {origin:{r:index, c:0}})
              index++;
            }
          }

          // Get sheet name with excel limitation check
          let sheetName = (userData.firstName + " " + userName)

          if (sheetName.length > 26) {
            sheetName = sheetName.substring(0, 26) + '...'
          }

          XLSX.utils.book_append_sheet(wb, sheet_user, sheetName)
        }
      }
    },
    onConfirmDelete(){
      this.$store.dispatch('Promotion/delete', this.itemID)
      this.$bvModal.hide('delete-modal-detail-promotion')
      this.goBack()
    },
    onSetSequenceStartDate({ sequenceID, startDate }) {
      // Try to find and update the sequence data
      for (var i = 0; i < this.promotion.sequences.length; i++) {
        if (this.promotion.sequences[i].userID == null && this.promotion.sequences[i].id == sequenceID) {
          this.promotion.sequences[i].startDate = startDate
          return
        }
      }
      
      // If no data exist, create a default one
      this.promotion.sequences.push({
        id: sequenceID,
        startDate,
        endDate: null,
        userID: null,
      })
    },
    onSetSequenceEndDate({ sequenceID, endDate }) {
      // Try to find and update the sequence data
      for (var i = 0; i < this.promotion.sequences.length; i++) {
        if (this.promotion.sequences[i].userID == null && this.promotion.sequences[i].id == sequenceID) {
          this.promotion.sequences[i].endDate = endDate
          return
        }
      }
      
      // If no data exist, create a default one
      this.promotion.sequences.push({
        id: sequenceID,
        startDate: null,
        endDate,
        userID: null,
      })
    },
    onAddIntern(intern) {
      this.promotion.users.push({ id: intern.id })
    },
    onRemoveIntern(intern) {
      for (var i = 0; i < this.promotion.users.length; i++) {
        if (this.promotion.users[i].id == intern.id) {
          this.promotion.users.splice(i, 1)
          break
        }
      }
    },
    onAddInternSequence(internSequence) {
      this.promotion.sequences.push({
        id: internSequence.id,
        startDate: internSequence.startDate,
        endDate: internSequence.endDate,
        userID: internSequence.userID,
      })
    },
    onRemoveInternSequence(internSequence) {
      for (var i = 0; i < this.promotion.sequences.length; i++) {
        if (
          this.promotion.sequences[i].id == internSequence.id && 
          this.promotion.sequences[i].userID == internSequence.userID && 
          this.promotion.sequences[i].starDate == internSequence.starDate && 
          this.promotion.sequences[i].endDate == internSequence.endDate
        ) {
          this.promotion.sequences.splice(i, 1)
          break
        }
      }
    },
    async save() {
      if (!this.promotion.title || this.promotion.title == '') {
        this.$bvToast.toast('La promotion n\'a pas de titre', { title: `Erreur` })
        return
      }

      if (!this.promotion.course) {
        this.$bvToast.toast('Pas de parcours associé', { title: `Erreur` })
        return
      }

      const response = await this.dispatchStoreRequest('Promotion/save', this.promotion)

      this.$bvToast.toast('Vos modifications ont bien été enregistrés !', { title: `Succès !` })

      if (response.id) {
        this.$router.push({
          name: 'promotions-edit',
          params: {
            itemID: response.id,
          }
        })
      }

      if (this.$refs.dashboard) {
        this.$refs.dashboard.loadData(true)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tabs-triggers {
  button {
    @apply flex-1 p-2 font-principal-medium;

    &.selected {
      @apply text-white bg-red;
    }
  }
}
</style>